import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Globe, Loader2, Sparkles } from "lucide-react";
import {
  applyWizardSuggestions,
  getWizardScan,
  pollWizardEvents,
  startWizardScan,
} from "../api/wizard.js";
import { fetchLocations } from "../api/monitors.js";

// ---------------------------------------------------------------------------
// Per-phase "juggling words" — these rotate while the phase runs so the user
// always sees motion even when the underlying step is slow.
// ---------------------------------------------------------------------------

const PHASE_LABELS = {
  queued: "Warming up",
  detect_platform: "Sniffing platform",
  browse_homepage: "Visiting your site",
  discover_pages: "Mapping key pages",
  analyze_third_parties: "Spotting third parties",
  ready: "Thinking like a store owner",
  suggesting: "Drafting your monitor plan",
  complete: "Done",
};

const JUGGLE_WORDS = {
  queued: ["Booking an agent", "Finding the closest region", "Heating up Chromium"],
  detect_platform: [
    "Checking the headers",
    "Looking for shopify-ish vibes",
    "Asking WordPress nicely",
  ],
  browse_homepage: [
    "Loading the homepage",
    "Watching the network",
    "Counting requests",
    "Letting late pixels fire",
  ],
  discover_pages: [
    "Reading the sitemap",
    "Walking products and collections",
    "Catalog tour",
    "Listing the important pages",
  ],
  analyze_third_parties: [
    "Spotting trackers",
    "Counting pixels",
    "Weighing Klaviyo",
    "Tagging payment providers",
  ],
  ready: [
    "Ranking what matters",
    "Drafting suggestions",
    "Picking the critical few",
    "Polishing recommendations",
  ],
  suggesting: [
    "Asking the model",
    "Weighing the critical few",
    "Sanity-checking targets",
    "Almost there",
  ],
};

const PHASE_ORDER = [
  "queued",
  "detect_platform",
  "browse_homepage",
  "discover_pages",
  "analyze_third_parties",
  "ready",
  "suggesting",
];

const PRIORITY_STYLES = {
  critical: "bg-red-100 text-red-700 ring-red-200",
  important: "bg-amber-100 text-amber-700 ring-amber-200",
  "nice-to-have": "bg-gray-100 text-gray-600 ring-gray-200",
};

const PRIORITY_SECTIONS = [
  { key: "critical", label: "Critical — start here" },
  { key: "important", label: "Important" },
  { key: "nice-to-have", label: "Nice to have" },
];

/**
 * Derive a short "We found these risks" list from the suggestion set.
 * Pure function so the UI test surface is just (suggestions) -> [string].
 */
function deriveRisks(suggestions) {
  const present = new Set((suggestions || []).map((s) => s.type));
  const risks = [];
  if (!present.has("ssl")) {
    risks.push("No SSL monitor — silent expiry would take the site offline without warning.");
  }
  if (!present.has("domain")) {
    risks.push("No domain expiry monitor — registrar lapses are the worst kind of outage.");
  }
  if (!present.has("dns")) {
    risks.push("DNS resolution isn't monitored separately — outages would look like generic failures.");
  }
  if (!present.has("visual_change")) {
    risks.push("No visual change watch — unexpected layout or pricing changes will go unnoticed.");
  }
  return risks;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Wizard() {
  const [url, setUrl] = useState("");
  const [step, setStep] = useState("intake"); // 'intake' | 'scanning' | 'suggestions'
  const [jobId, setJobId] = useState(null);
  const [phase, setPhase] = useState("queued");
  const [phaseHistory, setPhaseHistory] = useState({}); // { phase: { status, data } }
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState(new Set());
  const [error, setError] = useState(null);
  const [applying, setApplying] = useState(false);
  const startedAtRef = useRef(0);
  const navigate = useNavigate();

  // ----- juggling-words ticker --------------------------------------------
  const [wordIndex, setWordIndex] = useState(0);
  const phaseWords = useMemo(
    () => JUGGLE_WORDS[phase] || JUGGLE_WORDS.ready,
    [phase],
  );
  useEffect(() => {
    if (step !== "scanning") return;
    setWordIndex(0);
    const interval = setInterval(() => {
      setWordIndex((i) => (i + 1) % phaseWords.length);
    }, 1200);
    return () => clearInterval(interval);
  }, [phase, step, phaseWords.length]);

  // ----- form submit ------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();
    if (!url.trim()) return;
    setError(null);
    try {
      const { job_id } = await startWizardScan(url.trim());
      setJobId(job_id);
      setStep("scanning");
      startedAtRef.current = Date.now();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to start scan";
      setError(msg);
      toast.error(msg);
    }
  }

  // ----- polling lifecycle ----------------------------------------------
  useEffect(() => {
    if (step !== "scanning" || !jobId) return;
    let cancelled = false;
    let finalizedOnce = false;

    const poller = pollWizardEvents(
      jobId,
      (evt) => {
        if (cancelled) return;
        setPhase(evt.phase);
        setPhaseHistory((prev) => ({
          ...prev,
          [evt.phase]: { status: evt.status, data: evt.data },
        }));
        if (evt.phase === "complete" && !finalizedOnce) {
          finalizedOnce = true;
          const data = evt.data || {};
          if (Array.isArray(data.suggestions) && data.suggestions.length) {
            finalizeWithSuggestions(data.suggestions, data.platform);
          } else {
            // Buffered events may have expired; pull final state directly.
            (async () => {
              try {
                const scan = await getWizardScan(jobId);
                finalizeWithSuggestions(scan.suggestions || [], scan.platform);
              } catch {
                setError("Scan completed but suggestions failed to load.");
              }
            })();
          }
        }
      },
      ({ status }) => {
        // Server reports terminal status but we never saw a 'complete'
        // event — fetch the final scan record so we still resolve.
        if (cancelled || finalizedOnce) return;
        if (status !== "completed" && status !== "failed") return;
        finalizedOnce = true;
        (async () => {
          try {
            const scan = await getWizardScan(jobId);
            finalizeWithSuggestions(scan.suggestions || [], scan.platform);
          } catch {
            setError("Scan completed but suggestions failed to load.");
          }
        })();
      },
      (err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.error || err?.message || "Network error";
        setError(msg);
        toast.error(msg);
      },
    );

    return () => {
      cancelled = true;
      poller.stop();
    };
  }, [step, jobId]);

  function finalizeWithSuggestions(list, platform) {
    // Enforce a minimum 4s on the loading screen so the user has time to
    // read what we found (otherwise it flashes by).
    const MIN_MS = 4000;
    const elapsed = Date.now() - (startedAtRef.current || Date.now());
    const wait = Math.max(0, MIN_MS - elapsed);

    // Fetch locations in parallel with the loading-screen hold so the picker
    // is ready the instant we transition.
    const locsPromise = fetchLocations().catch(() => []);

    setTimeout(async () => {
      const locs = await locsPromise;
      setLocations(locs || []);
      setSelectedLocations(new Set(locs && locs[0] ? [locs[0].slug] : []));
      setSuggestions(list);
      const sel = new Set();
      list.forEach((s, i) => {
        if (s.priority === "critical" || s.priority === "important") sel.add(i);
      });
      setSelected(sel);
      setStep("suggestions");
    }, wait);
  }

  // ----- apply ------------------------------------------------------------
  async function handleApply() {
    if (!selected.size) {
      toast.info("Pick at least one monitor to create.");
      return;
    }
    if (!selectedLocations.size) {
      toast.info("Pick at least one location to run from.");
      return;
    }
    setApplying(true);
    try {
      const { count, site } = await applyWizardSuggestions(
        jobId,
        [...selected],
        [...selectedLocations],
      );
      const monitorsLabel = `${count} monitor${count === 1 ? "" : "s"}`;
      toast.success(
        site?.name
          ? `Created ${monitorsLabel} for site "${site.name}".`
          : `Created ${monitorsLabel}.`,
      );
      navigate(site?.id ? `/sites/${site.id}` : "/monitors");
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to create monitors";
      toast.error(msg);
    } finally {
      setApplying(false);
    }
  }

  // ----- render -----------------------------------------------------------
  if (step === "intake") {
    return (
      <div className="mx-auto max-w-2xl pt-12">
        <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
          <div className="mb-6 flex items-center gap-3">
            <div className="rounded-full bg-teal-50 p-2.5 text-teal-600">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Smart Setup</h1>
              <p className="text-sm text-gray-500">
                We'll scan your site and suggest the monitors that matter most.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-gray-700">
                Your website URL
              </span>
              <div className="flex items-center rounded-lg border border-gray-300 bg-white pl-3 focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500">
                <Globe className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  placeholder="example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full bg-transparent px-3 py-2.5 text-sm outline-none"
                />
              </div>
            </label>
            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={!url.trim()}
              className="w-full rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Scan my site
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (step === "scanning") {
    return <ScanningView phase={phase} phaseHistory={phaseHistory} word={phaseWords[wordIndex]} />;
  }

  return (
    <SuggestionsView
      suggestions={suggestions}
      selected={selected}
      onToggle={(i) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(i)) next.delete(i);
          else next.add(i);
          return next;
        })
      }
      locations={locations}
      selectedLocations={selectedLocations}
      onToggleLocation={(slug) =>
        setSelectedLocations((prev) => {
          const next = new Set(prev);
          if (next.has(slug)) next.delete(slug);
          else next.add(slug);
          return next;
        })
      }
      onApply={handleApply}
      applying={applying}
    />
  );
}

// ---------------------------------------------------------------------------
// Scanning view — phase progress + juggling words
// ---------------------------------------------------------------------------

function ScanningView({ phase, phaseHistory, word }) {
  const detected = phaseHistory.detect_platform?.data;
  const homepage = phaseHistory.browse_homepage?.data;
  const pages = phaseHistory.discover_pages?.data;
  const tps = phaseHistory.analyze_third_parties?.data;

  const factoid = [
    detected?.platform && detected.platform !== "unknown"
      ? `Found: ${cap(detected.platform)} site`
      : null,
    detected?.title ? `"${detected.title}"` : null,
    homepage?.request_count ? `${homepage.request_count} network requests` : null,
    pages?.total ? `${pages.total} pages discovered` : null,
    tps?.total ? `${tps.total} third parties` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center pt-16 text-center">
      <div className="relative">
        <div className="absolute inset-0 animate-ping rounded-full bg-teal-200 opacity-30" />
        <div className="relative rounded-full bg-teal-50 p-5 text-teal-600">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>

      <h2 className="mt-6 text-2xl font-semibold text-gray-900">
        {PHASE_LABELS[phase] || "Working"}
      </h2>
      <p key={word} className="mt-1 animate-fade-in text-sm text-gray-500">
        {word}…
      </p>

      {factoid && (
        <p className="mt-5 text-sm font-medium text-gray-700">{factoid}</p>
      )}

      <div className="mt-10 w-full max-w-md">
        <ol className="space-y-2.5">
          {PHASE_ORDER.map((p) => {
            const state =
              phaseHistory[p]?.status === "done"
                ? "done"
                : p === phase
                  ? "active"
                  : "pending";
            return (
              <li
                key={p}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-left"
              >
                {state === "done" ? (
                  <CheckCircle2 className="h-4 w-4 text-teal-600" />
                ) : state === "active" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-teal-600" />
                ) : (
                  <span className="block h-2 w-2 rounded-full bg-gray-300" />
                )}
                <span
                  className={
                    state === "pending"
                      ? "text-sm text-gray-400"
                      : "text-sm text-gray-700"
                  }
                >
                  {PHASE_LABELS[p]}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggestions view
// ---------------------------------------------------------------------------

function SuggestionsView({
  suggestions,
  selected,
  onToggle,
  locations,
  selectedLocations,
  onToggleLocation,
  onApply,
  applying,
}) {
  if (!suggestions.length) {
    return (
      <div className="mx-auto max-w-2xl pt-16 text-center">
        <h2 className="text-xl font-semibold text-gray-900">No suggestions yet</h2>
        <p className="mt-2 text-sm text-gray-500">
          The scan finished but didn't return any monitor recommendations.
        </p>
      </div>
    );
  }

  const risks = deriveRisks(suggestions);
  const indexed = suggestions.map((s, i) => ({ ...s, _idx: i }));
  const byPriority = PRIORITY_SECTIONS.map((sec) => ({
    ...sec,
    items: indexed.filter(
      (s) => (s.priority || "important") === sec.key,
    ),
  })).filter((sec) => sec.items.length > 0);

  function renderItem(s) {
    const isOn = selected.has(s._idx);
    return (
      <li
        key={`${s.type}-${s._idx}`}
        onClick={() => onToggle(s._idx)}
        className={`cursor-pointer rounded-xl border p-4 transition ${
          isOn
            ? "border-teal-500 bg-teal-50/40 ring-1 ring-teal-200"
            : "border-gray-200 bg-white hover:border-gray-300"
        }`}
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isOn}
            readOnly
            className="mt-1 h-4 w-4 cursor-pointer accent-teal-600"
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-gray-900">{s.name}</h3>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${
                  PRIORITY_STYLES[s.priority] || PRIORITY_STYLES.important
                }`}
              >
                {s.priority}
              </span>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                {s.type}
              </span>
              <span className="text-[11px] text-gray-500">
                every {formatInterval(s.interval_seconds)}
              </span>
            </div>
            <p
              className="mt-1 truncate font-mono text-xs text-gray-500"
              title={s.target}
            >
              {prettyTarget(s.target)}
            </p>
            <p className="mt-2 text-sm text-gray-700">{s.rationale}</p>
          </div>
        </div>
      </li>
    );
  }

  return (
    <div className="mx-auto max-w-3xl pt-6">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-gray-900">
          We'd recommend these monitors
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Tap to toggle. Selected ones will be created when you click Apply.
        </p>
      </div>

      {risks.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h2 className="text-sm font-semibold text-amber-800">
            We found these risks
          </h2>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {risks.map((r, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-amber-600">•</span>
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-6">
        {byPriority.map((sec) => (
          <div key={sec.key}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {sec.label}
            </h2>
            <ul className="space-y-3">{sec.items.map(renderItem)}</ul>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Run from</h3>
          <span className="text-xs text-gray-500">
            {selectedLocations.size} selected
          </span>
        </div>
        {locations.length === 0 ? (
          <p className="text-xs text-gray-500">
            No active locations available. Add one in Settings before applying.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {locations.map((loc) => {
              const isOn = selectedLocations.has(loc.slug);
              return (
                <button
                  key={loc.slug}
                  type="button"
                  onClick={() => onToggleLocation(loc.slug)}
                  className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${
                    isOn
                      ? "bg-teal-600 text-white ring-teal-600"
                      : "bg-white text-gray-700 ring-gray-300 hover:ring-gray-400"
                  }`}
                >
                  {loc.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {selected.size} of {suggestions.length} selected
        </p>
        <button
          onClick={onApply}
          disabled={!selected.size || !selectedLocations.size || applying}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {applying && <Loader2 className="h-4 w-4 animate-spin" />}
          Create {selected.size} monitor{selected.size === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function cap(s) {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}

function formatInterval(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

/**
 * Make a target URL/host display-friendly:
 *  - drop the scheme
 *  - drop a leading "www."
 *  - if the resulting string is longer than `max`, keep the host and
 *    middle-truncate the path: "shop.com/products/very-long…?v=123"
 * Always pair with `title={fullTarget}` on the element so the full value
 * is reachable on hover.
 */
function prettyTarget(target, max = 56) {
  if (!target) return "";
  let out = String(target).replace(/^https?:\/\//i, "");
  out = out.replace(/^www\./i, "");
  if (out.length <= max) return out;

  const slash = out.indexOf("/");
  if (slash === -1) return out.slice(0, max - 1) + "…";

  const host = out.slice(0, slash);
  const path = out.slice(slash); // leading "/"
  const budget = Math.max(8, max - host.length - 1); // 1 for the ellipsis
  if (path.length <= budget) return host + path;
  const head = Math.ceil(budget * 0.55);
  const tail = budget - head - 1;
  return host + path.slice(0, head) + "…" + path.slice(-tail);
}
